import { useAssets, useAssetTypes } from '../api/queries'
import { Button } from './ui/button'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { mdiPlus, mdiRefresh } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useTranslation } from 'react-i18next'
import { useLocale } from '../contexts/LocaleContext'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { Card } from './ui/card'
import { AssetName } from './ArchivedMarker'


export function AssetsAllPage() {
  const { t } = useTranslation('assets')
  const { locale } = useLocale()
  const navigate = useNavigate()
  const search = useSearch({ from: '/$org/assets/all' })
  const selectedTypeUid = search.asset_type_uid || ''
  const orgCode = 'demo'

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: t('common:nav.home'), path: '/demo' },
    { title: t('common:nav.assets') }
  ])

  // Use React Query hooks
  const {
    data: assetTypes = [],
    isLoading: assetTypesLoading,
    error: assetTypesError
  } = useAssetTypes(orgCode)

  const {
    data: assets = [],
    isLoading: assetsLoading,
    error: assetsError,
    refetch: refetchAssets
  } = useAssets(orgCode, selectedTypeUid || undefined)

  const loading = assetTypesLoading || assetsLoading
  const error = assetTypesError || assetsError

  const getAssetTypeName = (asset_type_uid: string) => {
    const assetTypeObj = assetTypes.find(type => type.uid === asset_type_uid)
    if (!assetTypeObj) {
      return t('list.unknown_type')
    }
    if (typeof assetTypeObj.name === 'string') {
      return assetTypeObj.name
    }
    return assetTypeObj.name[locale] || assetTypeObj.name['en'] || assetTypeObj.name['fr'] || assetTypeObj.code
  }

  const handleRefresh = () => {
    refetchAssets()
  }

  const handleTypeFilterChange = (typeUid: string) => {
    navigate({
      to: '/$org/assets',
      params: { org: orgCode },
      search: { showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: typeUid || undefined } as any
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={SECTION_ICONS.assets}
        title={t('list.title_all')}
        description={t('list.subtitle_all')}
        actions={
          <>
          <Can perm="assets.write">
            <Link
              to="/$org/createBuildingGroup"
              params={{ org: 'demo' }}
              search={{}}
              aria-label={t('common:actions.create_asset', 'Create an asset')}
              title={t('common:actions.create_asset', 'Create an asset')}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              data-testid="create-asset-btn"
            >
              <AppIcon path={mdiPlus} size="md" />
            </Link>
          </Can>
          <Button onClick={handleRefresh} variant="outline" disabled={loading}>
            <AppIcon path={mdiRefresh} size="md" className="mr-1" />
            {loading ? t('common:status.loading') : t('import.refresh')}
          </Button>
          </>
        }
      />

      {/* Type Filter */}
      <div className="bg-card p-4 rounded-lg border border-border">
        <label htmlFor="typeFilter" className="block text-sm font-medium text-foreground mb-2">
          {t('list.filter_by_type')}
        </label>
        <select
          id="typeFilter"
          value={selectedTypeUid}
          onChange={(e) => handleTypeFilterChange(e.target.value)}
          className="block w-full max-w-xs px-3 py-2 border border-border bg-background text-foreground rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
          disabled={assetTypesLoading}
        >
          <option value="">{t('list.all_types')}</option>
          {assetTypes.map((type) => (
            <option key={type.uid} value={type.uid}>
              {typeof type.name === 'string' ? type.name : (type.name[locale] || type.name['en'] || type.name['fr'] || t('list.unknown_type'))}
            </option>
          ))}
        </select>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-destructive text-sm">
            {(error as any)?.title || (error as any)?.message || t('common:status.error')}
          </div>
        </div>
      )}

      {/* Assets Table */}
      <Card className="overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_name')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_type')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_created')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-muted-foreground">
                  {t('list.loading_assets')}
                </td>
              </tr>
            ) : assets.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6">
                  <EmptyState
                    variant="inline"
                    live
                    icon={SECTION_ICONS.assets}
                    title={t('list.no_assets_found')}
                  />
                </td>
              </tr>
            ) : (
              assets.map((asset) => (
                <tr key={asset.uid} className="hover:bg-muted/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    <Link
                      to="/$org/assets/$assetUid"
                      params={{ org: 'demo', assetUid: asset.uid }}
                      className="text-primary hover:text-primary/80 hover:underline"
                    >
                      <AssetName name={asset.name ?? ''} archivedAt={(asset as { archived_at?: string | null }).archived_at} />
                    </Link>
                    {(asset as any).quantity > 1 && (
                      <span className="ml-2 text-muted-foreground" data-testid="asset-quantity-badge">
                        {t('detail.quantity_display', { count: (asset as any).quantity })}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {getAssetTypeName(asset.asset_type_uid)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {asset.created_at ? new Date(asset.created_at).toLocaleDateString('fr') : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* Assets Count */}
      <div className="text-sm text-muted-foreground">
        {assets.length > 0 && (
          <p>{t('list.count', { count: assets.length })}</p>
        )}
      </div>
    </div >
  )
}